import { BUILD_VERSION } from "@/lib/build-version";

/**
 * Journalisation des erreurs runtime côté navigateur.
 *
 * Chaque erreur (JS globale, promesse rejetée, error boundary React) est
 * envoyée à `/api/public/client-error` avec la route, la stack trace et le
 * hash du build — de quoi identifier immédiatement la cause d'un écran gris.
 */

export type ClientErrorSource =
  | "window.onerror"
  | "unhandledrejection"
  | "react_error_boundary"
  | "manual";

export type ClientErrorPayload = {
  message: string;
  stack?: string | undefined;
  source: ClientErrorSource;
  route: string;
  href: string;
  build: string;
  filename?: string | undefined;
  line?: number | undefined;
  column?: number | undefined;
  componentStack?: string | undefined;
  userAgent: string;
  viewport: string;
  online: boolean;
  referrer?: string | undefined;
  timestamp: string;
};

const ENDPOINT = "/api/public/client-error";
const MAX_PER_SESSION = 10;

let sent = 0;
const seen = new Set<string>();

function truncate(value: string | undefined, max: number) {
  if (!value) return undefined;
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function toPayload(
  error: unknown,
  source: ClientErrorSource,
  extra: Partial<ClientErrorPayload> = {},
): ClientErrorPayload {
  const err = error instanceof Error ? error : undefined;
  return {
    message: truncate(err?.message ?? String(error ?? "Unknown error"), 500)!,
    stack: truncate(err?.stack, 4000),
    source,
    route: window.location.pathname + window.location.search,
    href: window.location.href,
    build: BUILD_VERSION,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
    online: navigator.onLine,
    referrer: document.referrer || undefined,
    timestamp: new Date().toISOString(),
    ...extra,
  };
}

/** Envoie une erreur au serveur (best-effort, jamais bloquant). */
export function reportClientError(
  error: unknown,
  source: ClientErrorSource = "manual",
  extra: Partial<ClientErrorPayload> = {},
) {
  if (typeof window === "undefined") return;

  const payload = toPayload(error, source, extra);
  const key = `${payload.source}|${payload.message}|${payload.route}`;
  if (seen.has(key) || sent >= MAX_PER_SESSION) return;
  seen.add(key);
  sent += 1;

  // eslint-disable-next-line no-console
  console.error(`[client-error] ${payload.source} @ ${payload.route}`, payload);

  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    /* fallback fetch */
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

/** Écouteurs globaux : erreurs JS non gérées et promesses rejetées. */
export function installClientErrorLogging() {
  if (typeof window === "undefined") return;
  const w = window as Window & { __sunsetClientErrorLog?: boolean };
  if (w.__sunsetClientErrorLog) return;
  w.__sunsetClientErrorLog = true;

  const onError = (event: ErrorEvent) => {
    // Les erreurs de ressources (script/css) sont déjà traitées ailleurs.
    if (!(event instanceof ErrorEvent) || event.target !== window) return;
    reportClientError(event.error ?? event.message, "window.onerror", {
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, "unhandledrejection");
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
